import { useState, useEffect } from 'react';
import { C, t } from '../constants/dashboardConstants';

/** 화면 키 ↔ 서버 필드 */
const CHANNEL_FIELDS = { email: 'emailNotification', push: 'pushNotification', sms: 'smsNotification' };
const TYPE_FIELDS = { casting: 'castingNotification', message: 'messageNotification', marketing: 'marketingNotification' };
const ALL_FIELDS = [...Object.values(CHANNEL_FIELDS), ...Object.values(TYPE_FIELDS)];

/** 서버에 저장된 값이 없을 때 쓰는 기본값 (가입 직후 상태) */
const DEFAULTS = {
  emailNotification: true,
  pushNotification: true,
  smsNotification: false,
  castingNotification: true,
  messageNotification: false,
  marketingNotification: false,
};

/**
 * 여러 응답 중 알림 값이 실린 곳을 찾아 모은다.
 *
 * 값은 GET /api/talent/account가 정답이지만, 그 응답에 없는 필드가 있거나
 * 배포 전이라 경로가 없을 수 있어 사용자·프로필 응답도 함께 훑는다.
 * 하나도 못 찾으면 null — '모른다'를 기본값으로 위장하지 않는다.
 */
export function readNotificationFlags(...sources) {
  const found = {};
  sources.forEach(src => {
    if (!src || typeof src !== 'object') return;
    ALL_FIELDS.forEach(key => {
      if (found[key] === undefined && typeof src[key] === 'boolean') found[key] = src[key];
    });
  });
  return Object.keys(found).length > 0 ? found : null;
}

function Toggle({ on, onToggle, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      style={{
        width: '57.39px', height: '30px', borderRadius: '15px', border: 'none', padding: 0,
        background: on
          ? 'linear-gradient(90deg, #FFD92B 0%, #FFA40D 100%)'
          : '#A9A9B2',
        cursor: disabled ? 'default' : 'pointer', position: 'relative', flexShrink: 0,
        transition: 'background 0.2s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{
        position: 'absolute', top: '3.5px',
        left: on ? 'calc(100% - 26.5px)' : '3.5px',
        width: '23px', height: '23px', borderRadius: '50%',
        background: C.white,
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </button>
  );
}

function ToggleCard({ title, subtitle, on, onToggle, disabled }) {
  return (
    <div style={{
      background: on ? C.white : C.bg,
      border: `1px solid ${C.g200}`,
      borderRadius: '16px', padding: '24px',
      boxShadow: on
        ? '2px 2px 12px rgba(0,0,0,0.10)'
        : 'inset 0 2px 6px rgba(0,0,0,0.06)',
      display: 'flex', flexDirection: 'column',
      minHeight: '160px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
        <Toggle on={on} onToggle={onToggle} disabled={disabled} label={title} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: 'auto' }}>
        <span style={t('18px', 600, C.bk)}>{title}</span>
        <span style={t('14px', 400, C.sub)}>{subtitle}</span>
      </div>
    </div>
  );
}

function Notice({ tone = 'info', children }) {
  const danger = tone === 'danger';
  return (
    <div
      role={danger ? 'alert' : 'status'}
      style={{
        padding: '12px 16px', borderRadius: '12px',
        border: `1px solid ${danger ? '#FECACA' : C.g200}`,
        background: danger ? '#FEF2F2' : C.bg,
        ...t('14px', 400, danger ? '#B91C1C' : C.sub),
      }}
    >
      {children}
    </div>
  );
}

/**
 * 알림 수신 설정.
 *
 * 예전에는 토글 초기값이 하드코딩돼, 사용자가 끄고 저장해도 다시 들어오면 켜져 보였다.
 * 저장 실패도 조용히 삼켰다. 이 값을 알림 메일 발송 기준으로 쓰므로
 * 화면이 실제 저장 상태와 어긋나면 '껐는데 메일이 온다'가 된다.
 *
 * @param {object|null|undefined} initial  서버 값. undefined=불러오는 중, null=못 읽음
 * @param {(patch: object) => Promise<object|void>} [onChange]  실패하면 reject — 화면을 되돌린다.
 *   바뀐 뒤의 설정 전체를 돌려주면 그 값으로 화면을 맞춘다.
 *   넘기지 않으면 저장할 곳이 없다는 뜻이라 토글을 잠근다.
 */
export default function NotificationSettings({ initial, onChange }) {
  const loading = initial === undefined;
  const unreadable = initial === null;
  const canSave = typeof onChange === 'function';

  const [flags, setFlags] = useState({ ...DEFAULTS, ...(initial ?? {}) });
  const [saving, setSaving] = useState(null);   // 저장 중인 필드
  const [error, setError] = useState(null);

  // 서버 값이 늦게 도착하면 그때 맞춘다
  useEffect(() => {
    if (!initial) return;
    setFlags({ ...DEFAULTS, ...initial });
  }, [initial]);

  const toggle = async (field) => {
    if (!canSave || saving) return;
    const next = !flags[field];
    const before = flags;
    setFlags(prev => ({ ...prev, [field]: next }));
    setSaving(field);
    setError(null);
    try {
      // 서버는 보낸 값만 바꾼다. 그래서 바꾼 항목 하나만 보낸다 —
      // 화면의 기본값을 함께 보내면 아직 모르는 서버 값을 덮어쓰게 된다.
      const saved = await onChange({ [field]: next });
      // 저장 뒤의 설정 전체를 돌려주면 그게 정답이다 — 다른 기기에서 바꾼 값까지 여기서 맞춰진다
      const savedFlags = readNotificationFlags(saved);
      if (savedFlags) setFlags(prev => ({ ...prev, ...savedFlags }));
    } catch {
      // 저장이 안 됐는데 바뀐 것처럼 보이면 안 된다
      setFlags(before);
      setError('설정을 저장하지 못했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(null);
    }
  };

  const card = (field, title, subtitle) => (
    <ToggleCard
      key={field}
      title={title}
      subtitle={subtitle}
      on={flags[field]}
      disabled={loading || !canSave || saving !== null}
      onToggle={() => toggle(field)}
    />
  );

  // 좁은 화면에서는 한 줄에 하나씩
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' };

  return (
    <div style={{
      background: C.white, border: `1px solid ${C.g200}`,
      borderRadius: '20px', padding: 'clamp(20px, 4vw, 30px)',
      display: 'flex', flexDirection: 'column', gap: '30px',
    }}>
      {loading && <Notice>저장된 알림 설정을 불러오는 중입니다.</Notice>}
      {unreadable && canSave && (
        <Notice>저장된 설정을 불러오지 못해 기본값으로 표시합니다. 바꾸시면 그 값으로 저장됩니다.</Notice>
      )}
      {!canSave && <Notice>이 계정에서는 아직 알림 설정을 바꿀 수 없습니다.</Notice>}
      {error && <Notice tone="danger">{error}</Notice>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <span style={t('18px', 600, C.bk)}>알림 채널</span>
        <div style={grid}>
          {card(CHANNEL_FIELDS.email, '이메일 알림', '이메일로 알림을 받습니다.')}
          {card(CHANNEL_FIELDS.push, '푸시 알림', '브라우저 푸시 알림을 받습니다.')}
          {card(CHANNEL_FIELDS.sms, 'SMS 알림', '문자메시지로 알림을 받습니다.')}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <span style={t('18px', 600, C.bk)}>알림 유형</span>
        <div style={grid}>
          {card(TYPE_FIELDS.casting, '새로운 캐스팅 공고', '내 프로필에 맞는 새 공고가 올라올 때')}
          {card(TYPE_FIELDS.message, '메시지 알림', '새 메시지를 받을 때')}
          {card(TYPE_FIELDS.marketing, '마케팅 이메일', '프로모션 및 이벤트 안내')}
        </div>
      </div>
    </div>
  );
}
